import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Globe } from 'lucide-react';
import { instructorApi } from '../api/instructor';
import dayjs, { fmtTime, initials } from '../lib/dates';
import { InstructorLesson } from '../types';

const STATUS_LABEL: Record<string, string> = {
  scheduled: 'Planifiée',
  completed: 'Terminée',
  cancelled: 'Annulée',
};

function LessonItem({ lesson }: { lesson: InstructorLesson }) {
  const c = lesson.candidate;

  if (lesson.isOpenSlot) {
    return (
      <div className="rounded-2xl border-2 border-dashed border-green-400 bg-green-50/60 p-4 flex items-center gap-4">
        <div className="w-10 h-10 rounded-full bg-green-100 text-green-600 flex items-center justify-center flex-shrink-0">
          <Globe size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-green-700">Ouvert à la réservation</div>
          <div className="text-xs text-green-600">
            {fmtTime(lesson.startsAt)} – {fmtTime(lesson.endsAt)} · visible dans l'espace élève
          </div>
        </div>
        <span className="text-xs text-green-600 font-medium flex-shrink-0">Non réservé</span>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm flex items-center gap-4">
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 text-white flex items-center justify-center text-xs font-semibold flex-shrink-0">
        {c ? initials(c.firstName, c.lastName) : '–'}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-900">
          {c ? `${c.firstName} ${c.lastName}` : 'Élève'}
        </div>
        <div className="text-xs text-slate-500">
          {fmtTime(lesson.startsAt)} – {fmtTime(lesson.endsAt)} ·{' '}
          {lesson.lessonType ?? 'Conduite'} · {lesson.durationHours} h
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {lesson.bookedOnline && (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-50 text-green-700 border border-green-200 text-xs font-semibold">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
            Réservé en ligne
          </span>
        )}
        <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 border border-slate-200 text-xs font-semibold">
          {STATUS_LABEL[lesson.status] ?? lesson.status}
        </span>
      </div>
    </div>
  );
}

export default function PlanningView() {
  const [offset, setOffset] = useState(0);
  const weekRef = dayjs().add(offset, 'week').toISOString();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['planning', offset],
    queryFn: () => instructorApi.getPlanning(weekRef),
  });

  const allLessons = data?.days.flatMap((d) => d.lessons) ?? [];
  const lessonCount = allLessons.filter((l) => !l.isOpenSlot).length;
  const openSlotCount = allLessons.filter((l) => l.isOpenSlot).length;

  return (
    <section className="space-y-6">
      <div>
        <h1 className="text-slate-900">Mon planning</h1>
        <p className="text-slate-600">Vos leçons de la semaine.</p>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 px-5 py-3.5 shadow-sm flex items-center justify-between">
        <button
          onClick={() => setOffset((o) => o - 1)}
          className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
          aria-label="Semaine précédente"
        >
          <ChevronLeft size={18} className="text-slate-600" />
        </button>
        <div className="text-center">
          <div className="text-sm font-semibold text-slate-900">
            {data
              ? `Semaine du ${dayjs(data.weekStart).format('D')} au ${dayjs(
                  data.weekEnd,
                ).format('D MMMM YYYY')}`
              : '…'}
          </div>
          <div className="text-xs text-slate-500">
            {lessonCount} leçon(s)
            {openSlotCount > 0 && ` · ${openSlotCount} créneau(x) ouvert(s)`}
          </div>
        </div>
        <button
          onClick={() => setOffset((o) => o + 1)}
          className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
          aria-label="Semaine suivante"
        >
          <ChevronRight size={18} className="text-slate-600" />
        </button>
      </div>

      {isLoading && <p className="text-slate-500">Chargement du planning…</p>}
      {isError && (
        <p className="text-red-600">
          Impossible de charger le planning : {(error as Error).message}
        </p>
      )}

      {data && (
        <div className="space-y-6">
          {data.days.map((day) => (
            <div key={day.date}>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-sm font-semibold text-slate-900 capitalize">
                  {dayjs(day.date).format('dddd D MMMM')}
                </span>
                {dayjs(day.date).isSame(dayjs(), 'day') && (
                  <span className="px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-[11px] font-semibold">
                    Aujourd'hui
                  </span>
                )}
              </div>
              <div className="space-y-3">
                {day.lessons.length === 0 ? (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500 text-center">
                    Aucune leçon planifiée — journée libre
                  </div>
                ) : (
                  day.lessons.map((l) => <LessonItem key={l.id} lesson={l} />)
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
